// A small ASN.1 DER writer and reader: just what X.509 certificates and CMS signatures need (RFC 5280, RFC 5652).

export const concat = (...p: Uint8Array[]): Uint8Array => {
    const out = new Uint8Array(p.reduce((n, x) => n + x.length, 0));
    let o = 0;
    for (const x of p) { out.set(x, o); o += x.length; }
    return out;
};

function lengthBytes(n: number): Uint8Array {
    if (n < 0x80) return Uint8Array.of(n);
    const b: number[] = [];
    for (let v = n; v > 0; v = Math.floor(v / 256)) b.unshift(v & 255);
    return Uint8Array.of(0x80 | b.length, ...b);
}

export const tlv = (tag: number, ...content: Uint8Array[]): Uint8Array => {
    const body = concat(...content);
    return concat(Uint8Array.of(tag), lengthBytes(body.length), body);
};

export const seq = (...c: Uint8Array[]) => tlv(0x30, ...c);
export const set = (...c: Uint8Array[]) => tlv(0x31, ...c);
/** A SET OF, its members in DER order (sorted by encoding). */
export const setOf = (...c: Uint8Array[]) => {
    const sorted = [...c].sort((a, b) => { const n = Math.min(a.length, b.length); for (let i = 0; i < n; i++) if (a[i] !== b[i]) return a[i]! - b[i]!; return a.length - b.length; });
    return tlv(0x31, ...sorted);
};
export const octets = (b: Uint8Array) => tlv(0x04, b);
export const nul = () => Uint8Array.of(0x05, 0x00);
export const utf8 = (s: string) => tlv(0x0c, new TextEncoder().encode(s));
export const ia5 = (s: string) => tlv(0x16, new TextEncoder().encode(s));
export const bool = (v: boolean) => tlv(0x01, Uint8Array.of(v ? 0xff : 0));
/** Context-specific tag `n`; constructed by default. */
export const ctx = (n: number, content: Uint8Array, constructed = true) => tlv((constructed ? 0xa0 : 0x80) | n, content);
export const bitString = (b: Uint8Array, unused = 0) => tlv(0x03, Uint8Array.of(unused), b);

export function oid(dotted: string): Uint8Array {
    const p = dotted.split(".").map(Number);
    const out: number[] = [p[0]! * 40 + p[1]!];
    for (const v of p.slice(2)) {
        const b = [v & 0x7f];
        for (let x = Math.floor(v / 128); x > 0; x = Math.floor(x / 128)) b.unshift((x & 0x7f) | 0x80);
        out.push(...b);
    }
    return tlv(0x06, Uint8Array.from(out));
}

/** A non-negative INTEGER from big-endian bytes (a leading zero is added when the top bit is set). */
export function int(bytes: Uint8Array | number): Uint8Array {
    let b = typeof bytes === "number" ? Uint8Array.from(bytes === 0 ? [0] : (() => { const o: number[] = []; for (let v = bytes; v > 0; v = Math.floor(v / 256)) o.unshift(v & 255); return o; })()) : bytes;
    let i = 0;
    while (i < b.length - 1 && b[i] === 0) i++;
    b = b.subarray(i);
    return tlv(0x02, b[0]! & 0x80 ? concat(Uint8Array.of(0), b) : b);
}

const pad2 = (n: number) => String(n).padStart(2, "0");
/** UTCTime (years 1950–2049) or GeneralizedTime, as X.509 requires. */
export function time(d: Date): Uint8Array {
    const y = d.getUTCFullYear();
    const body = `${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}Z`;
    return y >= 1950 && y < 2050 ? tlv(0x17, new TextEncoder().encode(`${pad2(y % 100)}${body}`)) : tlv(0x18, new TextEncoder().encode(`${y}${body}`));
}

// ---- reading ------------------------------------------------------------------------------------------------------------------------

export interface Node { tag: number; /** the whole element */ raw: Uint8Array; /** its content */ body: Uint8Array; kids: Node[] | null }

const CONSTRUCTED = (tag: number) => (tag & 0x20) !== 0;

export function parse(buf: Uint8Array, start = 0, end = buf.length): Node[] {
    const out: Node[] = [];
    let p = start;
    while (p < end) {
        const s = p;
        const tag = buf[p++]!;
        let len = buf[p++]!;
        if (len & 0x80) {
            const n = len & 0x7f;
            if (n === 0 || n > 4) throw new Error("Unsupported length in the signature data.");
            len = 0;
            for (let i = 0; i < n; i++) len = len * 256 + buf[p++]!;
        }
        if (p + len > end) throw new Error("The signature data is cut short.");
        const body = buf.subarray(p, p + len);
        out.push({ tag, raw: buf.subarray(s, p + len), body, kids: CONSTRUCTED(tag) ? parse(buf, p, p + len) : null });
        p += len;
    }
    return out;
}

export function oidOf(n: Node): string {
    const b = n.body;
    const out: number[] = [Math.floor(b[0]! / 40), b[0]! % 40];
    let v = 0;
    for (let i = 1; i < b.length; i++) { v = v * 128 + (b[i]! & 0x7f); if (!(b[i]! & 0x80)) { out.push(v); v = 0; } }
    return out.join(".");
}

export function stringOf(n: Node): string {
    return new TextDecoder(n.tag === 0x1e ? "utf-16be" : n.tag === 0x0c ? "utf-8" : "latin1").decode(n.body);
}

export function dateOf(n: Node): Date | null {
    const s = new TextDecoder().decode(n.body);
    const m = n.tag === 0x17 ? /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?Z$/.exec(s) : /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?Z$/.exec(s);
    if (!m) return null;
    const y = n.tag === 0x17 ? (Number(m[1]) >= 50 ? 1900 : 2000) + Number(m[1]) : Number(m[1]);
    return new Date(Date.UTC(y, Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0)));
}
